import { Bundle } from "@/pages/bundles/types/bundle";

// ─── Offering-level config (shared across price versions) ─────────────────────
// A partial release clones an offering into several ACTIVE rows that differ ONLY
// in price. Everything listed here describes the OFFERING, so it must stay
// identical across those rows — otherwise the cohort that new signups resolve to
// (the newest release, see pickPriceVersion) keeps the pre-edit copy.
//
// Per-version, never shared: amount, single_session_amount (each version's own
// per-session price), released_at, status, old_amount — plus
// standard_id, which is the offering key itself.
//
// MIRRORED from apps/server/src/modules/api/bundle/bundle.service.ts
// (OFFERING_LEVEL_COLUMNS / OFFERING_LEVEL_METADATA_KEYS). The workspace has no
// shared package, so a field added on the server must be added here too, or the
// drift badge silently stops reporting it.
export const OFFERING_LEVEL_COLUMNS = [
  "name",
  "type",
  "currency",
  "payment_page_link",
  "payment_timing",
  "user_payment_type",
  "session_quantity",
  "session_types",
  "show_to_public",
  "total_allowed_user",
  "is_expiration_in_days",
  "expires_in_day",
  "expires_in_month",
  "product_id",
  "company_id",
  "reschedule_amount",
] as const;

export const OFFERING_LEVEL_METADATA_KEYS = [
  "tagline",
  "description",
  "features",
  "isMostPopular",
  "completion_cta",
  "care_team_actions",
  "payment_page",
  "reonboard_on_no_show",
] as const;

// Human labels for the drift list, so the UI can say "Tagline" instead of
// "metadata.tagline". Falls back to the raw key when unmapped.
const FIELD_LABELS: Record<string, string> = {
  name: "Name",
  type: "Type",
  currency: "Currency",
  payment_page_link: "Payment page link",
  payment_timing: "Payment timing",
  user_payment_type: "User payment type",
  session_quantity: "Session count",
  session_types: "Sessions",
  show_to_public: "Show to public",
  total_allowed_user: "Total allowed users",
  is_expiration_in_days: "Expiry unit",
  expires_in_day: "Expires in days",
  expires_in_month: "Expires in months",
  product_id: "Product",
  company_id: "Company",
  reschedule_amount: "Reschedule amount",
  "metadata.tagline": "Tagline",
  "metadata.description": "Description",
  "metadata.features": "Features",
  "metadata.isMostPopular": "Most popular",
  "metadata.completion_cta": "Completion CTA",
  "metadata.care_team_actions": "Care team actions",
  "metadata.payment_page": "Reschedule page copy",
  "metadata.reonboard_on_no_show": "Re-onboard on no-show",
};

export const offeringFieldLabel = (field: string): string =>
  FIELD_LABELS[field] ?? field;

// Stable structural comparison — session_types / features / completion_cta are
// JSON, so identity and key order can't be trusted. Sorts object keys recursively
// so `{a,b}` and `{b,a}` compare equal, and treats null/undefined alike.
function stableKey(value: unknown): string {
  const norm = (v: unknown): unknown => {
    if (v === null || v === undefined) return null;
    if (Array.isArray(v)) return v.map(norm);
    if (typeof v === "object") {
      return Object.keys(v as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, k) => {
          acc[k] = norm((v as Record<string, unknown>)[k]);
          return acc;
        }, {});
    }
    return v;
  };
  return JSON.stringify(norm(value));
}

// Which offering-level fields differ between two rows of the same offering.
// Returns dotted names for metadata keys. Mirrors the server's diffOfferingConfig
// so the badge and the server's `versions.drift` agree.
export function diffOfferingConfig(
  a: Partial<Bundle> | null | undefined,
  b: Partial<Bundle> | null | undefined,
): string[] {
  if (!a || !b) return [];
  const rowA = a as Record<string, unknown>;
  const rowB = b as Record<string, unknown>;
  const drift: string[] = [];

  for (const col of OFFERING_LEVEL_COLUMNS) {
    if (stableKey(rowA[col]) !== stableKey(rowB[col])) drift.push(col);
  }

  const metaA = (a.metadata ?? {}) as Record<string, unknown>;
  const metaB = (b.metadata ?? {}) as Record<string, unknown>;
  for (const key of OFFERING_LEVEL_METADATA_KEYS) {
    if (stableKey(metaA[key]) !== stableKey(metaB[key])) {
      drift.push(`metadata.${key}`);
    }
  }

  return drift;
}
